import { identity } from '$core/identity';
import { isSupabaseConfigured } from '$db/remote/supabase';
import { runSync, type SyncResult } from '$db/local/sync/engine';
import { getOutboxStats } from '$db/local/repositories/outbox.repo';
import { countPendingConflicts } from '$db/local/repositories/conflicts.repo';
import {
  getSyncMeta,
  lastPushAtKey
} from '$db/local/repositories/sync-meta.repo';

/**
 * Store reactivo del motor de sync (M4.3).
 * Muestra el estado al usuario y ofrece sync manual.
 */
class SyncStore {
  running = $state(false);
  lastSyncAt = $state<string | null>(null);
  pendingOps = $state(0);
  conflicts = $state(0);
  lastError = $state<string | null>(null);
  lastResult = $state<SyncResult | null>(null);

  get isGuest(): boolean {
    return identity.identity.kind === 'guest';
  }

  get canSync(): boolean {
    return !this.isGuest && isSupabaseConfigured();
  }

  get hasPending(): boolean {
    return this.pendingOps > 0;
  }

  get hasConflicts(): boolean {
    return this.conflicts > 0;
  }

  get hasError(): boolean {
    return this.lastError !== null;
  }

  async refresh(): Promise<void> {
    if (identity.identity.kind !== 'authenticated') {
      this.pendingOps = 0;
      this.conflicts = 0;
      this.lastSyncAt = null;
      return;
    }
    const userId = identity.identity.userId;
    const stats = await getOutboxStats();
    this.pendingOps = stats.count;
    this.conflicts = await countPendingConflicts();
    const last = await getSyncMeta<string>(lastPushAtKey(userId));
    this.lastSyncAt = last ?? null;
  }

  private suspendDepth = 0;

  /**
   * Suspende el auto-sync mientras se ejecutan operaciones masivas
   * (retry all, discard all, etc.). El sync manual puede seguir llamándose
   * pero syncNow() devolverá null sin ejecutar mientras suspendDepth > 0.
   */
  suspend(): void {
    this.suspendDepth += 1;
  }

  resume(): void {
    if (this.suspendDepth > 0) this.suspendDepth -= 1;
  }

  get isSuspended(): boolean {
    return this.suspendDepth > 0;
  }

  async syncNow(): Promise<SyncResult | null> {
    if (this.suspendDepth > 0) return null;
    if (this.running || !this.canSync) return null;
    this.running = true;
    this.lastError = null;
    try {
      const result = await runSync();
      this.lastResult = result;
      if (!result.ok && result.error) {
        this.lastError = result.error;
      }
      await this.refresh();
      return result;
    } catch (err) {
      this.lastError = err instanceof Error ? err.message : String(err);
      return null;
    } finally {
      this.running = false;
    }
  }

  async bootstrap(): Promise<void> {
    await this.refresh();
    if (this.canSync) {
      void this.syncNow();
    }
  }

  clearError(): void {
    this.lastError = null;
  }
}

export const syncStore = new SyncStore();

let onlineAttached = false;

export function attachOnlineSync(): void {
  if (onlineAttached || typeof window === 'undefined') return;
  onlineAttached = true;
  window.addEventListener('online', () => {
    if (syncStore.canSync) void syncStore.syncNow();
  });
}